import { inject, Injectable } from '@angular/core';
import { initialize, LDClient, LDFlagSet } from 'launchdarkly-js-client-sdk';
import { BehaviorSubject, combineLatest, first } from 'rxjs';
import { UserAccount } from '../../features/account/models/UserAccount';
import { ACCOUNT_INFO_TOKEN, API_KEY_TOKEN } from './feature-flagging.module';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlaggingService {
  private readonly _accountLogin$ = inject(ACCOUNT_INFO_TOKEN);
  private readonly _launchDarklyKey$ = inject(API_KEY_TOKEN);

  private readonly _flags: BehaviorSubject<LDFlagSet> = new BehaviorSubject({});
  readonly flags$ = this._flags.asObservable();

  private readonly _isLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly isLoaded$ = this._isLoaded.asObservable();

  ldClient: LDClient | undefined;

  constructor() {
    combineLatest([this._launchDarklyKey$, this._accountLogin$]).pipe(
      first(([key, account]) => !!key && !!account)
    ).subscribe(([key, account]) => {
      this._init(key, account);
    });
  }

  private _init(envKey: string, account: UserAccount) {
    const user = account?.username ? {
      kind: 'user',
      key: account.username,
      email: account.email,
      name: account.username,
      firstName: account.firstName,
      lastName: account.lastName
    } : {
      kind: 'user',
      key: 'anon',
      anonymous: true
    };
      
    this.ldClient = initialize(envKey, user);

    this.ldClient.on('change', (flags: { [key: string]: { current: boolean; previous: boolean } }) => {
      const newFlags = this._flags.value;
      for (const key in flags) {
        if (flags.hasOwnProperty(key)) {
          newFlags[key] = flags[key].current;
        }
      }
      this._flags.next(newFlags);
    });
      
    this.ldClient.on('ready', () => {
      this.setFlags();
      this._isLoaded.next(true);
    });
  }
  
  setFlags() {
    this._flags.next(this.ldClient?.allFlags() || {});
  }
}
